import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { FBaseService, collection } from '../services/firebase/f-base.service';
import { License } from '../../../../../models/Subject';

export const subjectLicenseGuard = async (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const fbService = inject(FBaseService);
  const router = inject(Router);

  try {
    let subjectId = getParamFromState(state, 'subjectId')!;
    let groupId = getParamFromState(state, 'groupId');
    if (!groupId) groupId = 'all';
    const subject = await firstValueFrom(fbService.getById(collection.subjects, subjectId));
    if (!subject) {
      return false;
    }

    const url = state.url.toLowerCase();
    const isHomeLearningConfiguration = /\/homelearning\/configuration(?:\/|$)/.test(url);
    // The configuration page is the PRD's disabled no-license state. Its parent
    // subject-owner guard still protects the route, and the page exposes no
    // settings controls when this license is absent.
    if (isHomeLearningConfiguration && !subject.license) return true;
    // The home learning list is the PRD's no-license state for this mode: let it
    // through without home learning so the page can explain the missing license.
    const isHomeLearningList = /\/homelearning\/?(?:[?#].*)?$/.test(url);
    if (isHomeLearningList && !licenseGrantsMode(subject.license, 'homelearning')) return true;
    if (!subject.license) return false;

    const license: License = subject.license;

    // Keep the configuration surface visible when this subject's license does
    // not include home learning; the component renders a read-only warning.
    if (isHomeLearningConfiguration && license !== 'HOME_LEARNING' && license !== 'MENTORING') return true;

    if (hasAccess(license, url)) {
      return true;
    } else {
      if (url.includes('homelearning') && license === 'HOME_LEARNING') {
        router.navigateByUrl('/subjects/' + subjectId + '/teaching/' + groupId + '/homelearning');
      } else {
        router.navigateByUrl('/subjects/' + subjectId + '/teaching/' + groupId + '/classworks');
      }
      return false;
    }

  } catch (error) {
    console.error('Subject license guard error:', error);
    return false;
  }
};

export function hasAccess(license: License, url: string): boolean {
  switch (license) {
    case 'CLASSWORK':
      return !url.includes('homeworks') && !url.includes('homelearning') && !url.includes('exams');

    case 'HOMEWORK':
      return !url.includes('homelearning');

    case 'HOME_LEARNING':
      return true;

    case 'MENTORING':
      return true;

    default:
      return false;
  }
}

export type LicenseMode = 'classworks' | 'homeworks' | 'exams' | 'homelearning';

/** Modes served by quiz packages (classwork, homework and exam tasks). */
export const PACKAGE_LICENSE_MODES: LicenseMode[] = ['classworks', 'homeworks', 'exams'];

/** Whether the license covers the given learning mode; uses the same rule as the route guard. */
export function licenseGrantsMode(license: License | undefined, mode: LicenseMode): boolean {
  return hasAccess(license ?? null, mode);
}

/** Whether the license covers at least one of the given learning modes. */
export function licenseGrantsAnyMode(license: License | undefined, modes: LicenseMode[]): boolean {
  return modes.some(mode => licenseGrantsMode(license, mode));
}

function getParamFromState(state: RouterStateSnapshot, key: string): string | null {
  return findParam(state.root, key);
}
function findParam(snap: ActivatedRouteSnapshot, key: string): string | null {
  const v = snap.paramMap.get(key);
  if (v) return v;
  for (const c of snap.children) {
    const r = findParam(c, key);
    if (r) return r;
  }
  return null;
}
